import { aiGenerationProgressKey } from './datasetOverviewProgress';
import {
    GENERATION_TYPE_FULL_SAMPLE,
    generationProgressBarItemFromStatus,
    generationSummaryExtra,
    inProgressStepPercent,
    isTrajectoryGenerationType,
    mergeGenerationProgressIntoStatus,
    normalizeGenerationProgressPayload,
} from './generationProgressStatus';
import { findRetrievalAgentProgressKeyForDataset, markRetrievalAgentTrajectoryGeneration } from './retrievalAgentProgress';
import { findLabelGenerationProgressKey, foldGenerationProgressIntoUploadItem } from './uploadProgress';

/**
 * The card that shows a generation run as one of its steps, with the run's
 * progress folded in, as `{ key, item }`: the upload or clone card running its
 * label generation, else (TRAJECTORY) the Retrieval Agent card that chained
 * it. Null when the run has its own ``ai_gen_*`` card, as a Generate-with-AI
 * (FULL_SAMPLE) run always does. Cards of the run's dataset come first: the
 * upload lookup otherwise falls back to the task's only label-generation card,
 * which may be an unrelated upload.
 */
export function foldGenerationIntoOwnerCard(progressStatus, { usecaseId, datasetName, generationType, foldOptions }) {
    if (generationType === GENERATION_TYPE_FULL_SAMPLE) return null;
    const excludeKey = aiGenerationProgressKey(usecaseId, datasetName);
    const sameDatasetKey = findLabelGenerationProgressKey(progressStatus, { datasetName, excludeKey });
    const retrievalKey = !sameDatasetKey && isTrajectoryGenerationType(generationType)
        ? findRetrievalAgentProgressKeyForDataset(progressStatus, datasetName)
        : null;
    const uploadKey = sameDatasetKey
        || (retrievalKey ? null : findLabelGenerationProgressKey(progressStatus, { usecaseId, datasetName, excludeKey }));
    if (uploadKey) {
        return { key: uploadKey, item: foldGenerationProgressIntoUploadItem(progressStatus[uploadKey], foldOptions) };
    }
    if (!retrievalKey) return null;
    return {
        key: retrievalKey,
        item: foldGenerationProgressIntoUploadItem(
            markRetrievalAgentTrajectoryGeneration(progressStatus[retrievalKey]),
            foldOptions,
        ),
    };
}

/**
 * Merges one active job from GET ``ai-generation/status/{usecase_id}`` where
 * its live ``updateGenerationProgress`` event would go: onto the card that
 * runs it as a step, else onto its own ``ai_gen_*`` card. A card that already
 * ended, or that shows more progress, is newer than the response and is kept.
 */
export function mergeActiveGenerationJob(progressStatus, usecaseId, job) {
    const item = generationProgressBarItemFromStatus({ ...job, active: true }, usecaseId);
    if (!item) return progressStatus;
    const isTrajectory = isTrajectoryGenerationType(job.generation_type);
    const pct = inProgressStepPercent(job.progress || 0);
    const owner = foldGenerationIntoOwnerCard(progressStatus, {
        usecaseId,
        datasetName: job.dataset_name,
        generationType: job.generation_type,
        foldOptions: {
            isFailed: false,
            isCompleted: false,
            pct,
            processedPrompts: job.processed_prompts || 0,
            totalPrompts: job.total_prompts || 0,
            phase: job.phase,
            warningFields: generationSummaryExtra(normalizeGenerationProgressPayload(job)),
            ...(isTrajectory ? { isTrajectory: true } : {}),
        },
    });
    const current = progressStatus?.[owner ? owner.key : aiGenerationProgressKey(usecaseId, item.datasetName)];
    if (current?.isCompleted || current?.isError) return progressStatus;
    if (!owner) return mergeGenerationProgressIntoStatus(progressStatus, item, usecaseId, item.datasetName);
    if ((Number(current.labelGenerationProgress) || 0) > pct) return progressStatus;
    return { ...progressStatus, [owner.key]: owner.item };
}
